import { useEffect, useRef, useState } from 'react'
import { Menu } from 'lucide-react'
import { navItems, UTILITY_LINKS } from '@/config/nav'
import { site } from '@/config/site'
import { Logo } from './Logo'
import { MobileMenu } from './MobileMenu'
import { AccessibilityMenu } from '@/components/a11y/AccessibilityMenu'
import { ButtonLink } from '@/components/ui/Button'
import { cn } from '@/lib/cn'


/**
 * Header. Desktop: a slim dark utility bar (h-9) over the white main bar
 * (h-20) = 7.25rem. Below lg the utility bar is dropped (its links live in the
 * mobile menu) and the main bar is h-16, so the header never eats more than
 * 64px of a phone screen. <main> in Layout reserves exactly these heights.
 */
export function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-40">
        {/* Utility bar — stays in place while scrolling (Website Review V2) */}
        <div className="hidden overflow-hidden bg-ink-deep text-surface/85 lg:block">
          <div className="mx-auto flex h-9 max-w-[88rem] items-center justify-between px-6 text-xs sm:px-10">
            <p className="tracking-wide">
              U.S. Chapter of GIA Longevity
            </p>
            <ul className="ml-auto flex items-center gap-5">
              {UTILITY_LINKS.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    className="inline-block py-2 font-semibold uppercase tracking-[0.1em] hover:text-gia-blue-light-2"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Main bar */}
        <div
          className={cn(
            'border-b bg-surface transition-shadow duration-300 ease-out-quart',
            scrolled
              ? 'border-hairline shadow-[0_8px_30px_-12px_rgb(44_41_38_/_0.18)]'
              : 'border-transparent',
          )}
        >
          <div className="mx-auto flex h-16 max-w-[88rem] items-center justify-between px-4 sm:px-10 lg:h-20">
            <Logo tone="dark" />

            <nav aria-label="Primary" data-primary-nav className="hidden xl:block">
              <ul className="flex items-center gap-1">
                {navItems.map((item) => (
                  <li key={item.id} className="group relative">
                    <a
                      href={item.href}
                      className="inline-block whitespace-nowrap px-4 py-3 text-sm font-bold uppercase tracking-[0.06em] text-ink-deep transition-colors hover:text-brand-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                    >
                      {item.label}
                    </a>
                    {/* Mega-menu preview */}
                    <div
                      role="tooltip"
                      className="pointer-events-none invisible absolute left-1/2 top-full z-40 mt-1 w-72 -translate-x-1/2 border border-hairline bg-surface p-4 opacity-0 shadow-[0_24px_60px_-24px_rgb(44_41_38_/_0.18)] transition-opacity duration-200 ease-out-quart group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
                    >
                      <span className="block text-sm leading-snug text-ink-slate">
                        {item.description}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="flex shrink-0 items-center gap-1 sm:gap-3">
              <AccessibilityMenu />
              <ButtonLink
                href={site.donateUrl}
                variant="primary"
                size="md"
                className="hidden sm:inline-flex"
              >
                Donate
              </ButtonLink>
              <button
                ref={triggerRef}
                type="button"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                aria-expanded={mobileOpen}
                aria-controls="mobile-menu"
                data-menu-button
                className="-mr-2 inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center text-ink-deep hover:bg-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand xl:hidden"
              >
                <Menu className="h-6 w-6" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </header>

      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        triggerRef={triggerRef}
      />
    </>
  )
}
